import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, ScanLine } from 'lucide-react';
import { identityService } from '../../services/api';
import { toErrorMessage } from '../../utils/safeStr';

/** Fields of /identity/by-phone and /identity/enrich-phone that we render. */
export interface PhoneOsint {
    found: boolean;
    names?: string[];
    emails?: string[];
    phones?: string[];
    addresses?: string[];
    address_objects?: Array<{
        formatted_address?: string;
        address_text?: string;
        address_text_masked?: string;
        city?: string;
        state?: string;
        pincode?: string;
        category?: string;
        confidence_score?: number;
    }>;
    sources?: string[];
    india_sources?: string[];
    hit_count?: number;
    checked_at?: string | null;
}

export const LIVE_OSINT_CONFIRM =
    'Run a live OSINT lookup (Outris) for this phone? This costs one paid API call. The result is cached afterwards.';

interface Props {
    phone: string;
    compact?: boolean;
}

/**
 * Per-phone OSINT: cached result loads automatically (free); the live paid
 * lookup is an explicit, confirmed click. Mirrors the P2P Intelligence dossier.
 */
export default function PhoneOsintPanel({ phone, compact = false }: Props) {
    const [osint, setOsint] = useState<PhoneOsint | null>(null);
    const [loading, setLoading] = useState(true);
    const [live, setLive] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [liveDone, setLiveDone] = useState(false);
    const phoneRef = useRef(phone);
    phoneRef.current = phone;

    // R8: cancelled flag so a stale phone never overwrites the current one.
    useEffect(() => {
        let cancelled = false;
        setOsint(null);
        setError(null);
        setLive(false);
        setLiveDone(false);
        setLoading(true);
        identityService.getByPhone(phone)
            .then((r: PhoneOsint) => { if (!cancelled) setOsint(r); })
            .catch(() => { if (!cancelled) setOsint({ found: false }); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [phone]);

    const runLive = useCallback(async () => {
        if (!window.confirm(LIVE_OSINT_CONFIRM)) return;
        const requested = phone;
        setLive(true);
        setError(null);
        try {
            const r: PhoneOsint = await identityService.enrichPhone(requested);
            if (phoneRef.current !== requested) return;
            setOsint(r);
            setLiveDone(true);
        } catch (e) {
            if (phoneRef.current !== requested) return;
            setError(toErrorMessage(e, 'Live lookup failed'));
        } finally {
            if (phoneRef.current === requested) setLive(false);
        }
    }, [phone]);

    const addrs: NonNullable<PhoneOsint['address_objects']> = osint?.found
        ? (osint.address_objects?.length
            ? osint.address_objects
            : (osint.addresses ?? []).map(a => ({ formatted_address: a })))
        : [];
    const sources = osint?.india_sources?.length ? osint.india_sources : osint?.sources;
    const text = compact ? 'text-[10px]' : 'text-[11px]';

    return (
        <div
            className={`bg-secondary/60 border border-border/60 rounded p-2 ${text}`}
            data-testid="phone-osint-panel"
            onClick={e => e.stopPropagation()}
        >
            <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-primary">{phone}</span>
                {loading && (
                    <span className="text-muted-foreground flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Checking cache…
                    </span>
                )}
                {!loading && osint?.found && (
                    <span className="text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded border border-green-500/30">
                        {osint.hit_count || (osint.sources?.length ?? 0)} hits{liveDone ? ' · live' : ' · cached'}
                    </span>
                )}
                {!loading && (
                    live ? (
                        <span className="ml-auto text-muted-foreground flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" /> Looking up…
                        </span>
                    ) : (
                        <button
                            type="button"
                            onClick={runLive}
                            className="ml-auto flex items-center gap-1 px-2 py-0.5 bg-primary text-primary-foreground rounded font-medium hover:bg-brand-gold-hover transition-colors"
                            title="Costs one paid API call (Outris). Cached afterwards."
                        >
                            <ScanLine className="w-3 h-3" /> Live OSINT check
                        </button>
                    )
                )}
            </div>
            {error && <div className="mt-1 text-red-400" role="alert">{error}</div>}
            {!loading && osint && !osint.found && !error && (
                <div className="mt-1 text-muted-foreground">No cached OSINT hits</div>
            )}
            {osint?.found && (
                <div className="mt-1.5 grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {!!osint.names?.length && (
                        <div><span className="text-muted-foreground">Names:</span> {osint.names.join(', ')}</div>
                    )}
                    {!!osint.emails?.length && (
                        <div><span className="text-muted-foreground">Emails:</span> <span className="font-mono">{osint.emails.join(', ')}</span></div>
                    )}
                    {!!osint.phones?.length && (
                        <div className="md:col-span-2">
                            <span className="text-muted-foreground">Alt phones:</span>{' '}
                            <span className="font-mono">{osint.phones.filter(p => p !== phone).join(', ') || '—'}</span>
                        </div>
                    )}
                    {addrs.length > 0 && (
                        <div className="md:col-span-2">
                            <span className="text-muted-foreground">Addresses:</span>
                            <ul className="mt-0.5 ml-3 list-disc">
                                {addrs.slice(0, 4).map((a, i) => (
                                    <li key={i}>
                                        {a.formatted_address || a.address_text || a.address_text_masked || '—'}
                                        {a.city && (
                                            <span className="text-muted-foreground"> · {a.city}{a.state ? ', ' + a.state : ''}{a.pincode ? ' ' + a.pincode : ''}</span>
                                        )}
                                        {a.category && <span className="text-amber-300 ml-1">[{a.category}]</span>}
                                        {typeof a.confidence_score === 'number' && (
                                            <span className="text-muted-foreground ml-1">{Math.round(a.confidence_score * 100)}%</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {!!sources?.length && (
                        <div className="md:col-span-2 text-muted-foreground">Sources: {sources.join(' · ')}</div>
                    )}
                </div>
            )}
        </div>
    );
}
